import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { SectionLoader } from "@/components/common";
import { RoleBadges } from "@/components/RoleBadges";
import ImportMembersDialog from "@/components/admin/ImportMembersDialog";
import { initials } from "@/lib/format";
import {
  Search, MoreHorizontal, Trash2, Lock, Mic, ShieldCheck, Star, Clapperboard, Loader2, Check, X, Settings2, UserPlus, MailCheck, KeyRound, Copy, Upload, MailPlus,
} from "lucide-react";

const ROLE_OPTIONS = [
  { key: "admin", label: "Admin", desc: "Full access to everything (a 'co-admin').", icon: ShieldCheck },
  { key: "core", label: "Core (Leadership)", desc: "Read-only members list + analytics.", icon: Star },
  { key: "producer", label: "Producer", desc: "Recordings, clips, mixer, videos & hub host controls.", icon: Clapperboard },
];

function RoleDialog({ member, actorIsOwner, onClose, onSaved }) {
  const open = !!member;
  const [roles, setRoles] = useState([]);
  const [eligible, setEligible] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (member) {
      setRoles(member.roles || []);
      setEligible(!!member.podcast_eligible);
    }
  }, [member]);

  if (!member) return null;

  const toggleRole = (key, on) => {
    setRoles((prev) => (on ? Array.from(new Set([...prev, key])) : prev.filter((r) => r !== key)));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/members/${member.user_id}/roles`, { roles, podcast_eligible: eligible });
      toast.success(`Updated ${member.name}'s access`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update roles");
    } finally {
      setSaving(false);
    }
  };

  // A co-admin can grant admin, but cannot toggle admin off of an existing admin (they couldn't open this anyway).
  const adminLocked = !actorIsOwner && (member.roles || []).includes("admin") && member.user_id !== member._self;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="role-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Manage access — {member.name}</DialogTitle>
          <DialogDescription>{member.email}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {ROLE_OPTIONS.map((opt) => {
            const checked = roles.includes(opt.key);
            const locked = opt.key === "admin" && adminLocked;
            return (
              <div key={opt.key} className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
                <div className="flex items-start gap-3">
                  <opt.icon className="mt-0.5 h-4 w-4 text-primary" />
                  <div>
                    <Label className="text-sm font-medium text-foreground">{opt.label}</Label>
                    <p className="text-xs text-muted-foreground">{opt.desc}</p>
                  </div>
                </div>
                <Switch
                  checked={checked}
                  disabled={locked}
                  onCheckedChange={(v) => toggleRole(opt.key, v)}
                  data-testid={`role-switch-${opt.key}`}
                />
              </div>
            );
          })}

          <div className="flex items-start justify-between gap-3 rounded-lg border border-[rgba(201,119,46,0.35)] bg-[rgba(201,119,46,0.08)] p-3">
            <div className="flex items-start gap-3">
              <Mic className="mt-0.5 h-4 w-4 text-[#8A4A18]" />
              <div>
                <Label className="text-sm font-medium text-foreground">Podcast eligible</Label>
                <p className="text-xs text-muted-foreground">Allows this member to RSVP for podcast seats.</p>
              </div>
            </div>
            <Switch checked={eligible} onCheckedChange={setEligible} data-testid="role-switch-podcast" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="role-dialog-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save access"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminMembers() {
  const { user, can, isOwner } = useAuth();
  const canManageRoles = can("manage_roles");
  const canRemove = can("manage_members");

  const [members, setMembers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [busyReq, setBusyReq] = useState(null);
  const [busyJoin, setBusyJoin] = useState(null);
  const [resetBusy, setResetBusy] = useState(null);
  const [tempPwInfo, setTempPwInfo] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const sendAllInvites = async (onlyUnsent) => {
    setBulkBusy(true);
    const fd = new FormData(); fd.append("only_unsent", onlyUnsent ? "true" : "false");
    try {
      const r = await api.post("/admin/members/import/send-pending-invites", fd);
      toast.success(r.data.queued ? `Sending ${r.data.queued} set-password email${r.data.queued === 1 ? "" : "s"} (about a second apart)` : "Nobody to email — everyone's covered");
      setTimeout(load, 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the links");
    } finally { setBulkBusy(false); }
  };

  const load = useCallback(async () => {
    const [ms, rq, jr] = await Promise.all([
      api.get("/admin/members").then((x) => x.data).catch(() => []),
      canManageRoles ? api.get("/admin/podcast-requests?status=pending").then((x) => x.data).catch(() => []) : Promise.resolve([]),
      canRemove ? api.get("/admin/join-requests?status=pending").then((x) => x.data).catch(() => []) : Promise.resolve([]),
    ]);
    setMembers(ms);
    setRequests(rq);
    setJoinRequests(jr);
    setLoading(false);
  }, [canManageRoles, canRemove]);
  useEffect(() => { load(); }, [load]);

  const approveJoin = async (r) => {
    setBusyJoin(r.user_id);
    try { await api.post(`/admin/members/${r.user_id}/approve`); toast.success(`${r.name} approved — they can now sign in`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not approve"); } finally { setBusyJoin(null); }
  };
  const declineJoin = async (r) => {
    setBusyJoin(r.user_id);
    try { await api.post(`/admin/members/${r.user_id}/decline`); toast.success("Request declined"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not decline"); } finally { setBusyJoin(null); }
  };

  const approveReq = async (req) => {
    setBusyReq(req.request_id);
    try { await api.post(`/admin/podcast-requests/${req.request_id}/approve`); toast.success(`${req.name} is now podcast-eligible`); await load(); }
    catch (e) { toast.error("Could not approve"); } finally { setBusyReq(null); }
  };
  const denyReq = async (req) => {
    setBusyReq(req.request_id);
    try { await api.post(`/admin/podcast-requests/${req.request_id}/deny`); toast.success("Request denied"); await load(); }
    catch (e) { toast.error("Could not deny"); } finally { setBusyReq(null); }
  };

  const remove = async (m) => {
    try { await api.delete(`/admin/members/${m.user_id}`); toast.success("Member removed"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };

  // Email the member a self-service reset link (locked-out recovery).
  const sendResetLink = async (m) => {
    setResetBusy(m.user_id);
    try {
      const r = await api.post(`/admin/members/${m.user_id}/send-reset`);
      toast.success(r.data?.message || `Reset link sent to ${m.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send a reset link");
    } finally { setResetBusy(null); }
  };

  // Email a 14-day "set your password" link to a member who has no password yet (imported accounts).
  const sendInviteLink = async (m) => {
    setResetBusy(m.user_id);
    try {
      const r = await api.post(`/admin/members/${m.user_id}/send-invite`);
      toast.success(r.data?.message || `Set-password link sent to ${m.email}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send a set-password link");
    } finally { setResetBusy(null); }
  };

  // Force a one-time temporary password and surface it so the organizer can relay it.
  const setTempPassword = async (m) => {
    setResetBusy(m.user_id);
    try {
      const r = await api.post(`/admin/members/${m.user_id}/set-temp-password`);
      setTempPwInfo({ name: m.name, email: r.data?.email || m.email, temp_password: r.data?.temp_password, emailed: !!r.data?.emailed });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set a temporary password");
    } finally { setResetBusy(null); }
  };

  const copyTempPassword = async () => {
    if (!tempPwInfo?.temp_password) return;
    try {
      await navigator.clipboard.writeText(tempPwInfo.temp_password);
      toast.success("Temporary password copied");
    } catch (e) {
      toast.error("Couldn't copy — select and copy it manually");
    }
  };

  const isSelf = (m) => m.user_id === user?.user_id;
  const canEditTarget = (m) => {
    if (!canManageRoles) return false;
    if (m.is_owner) return false;                              // owner's roles are fixed
    if (!isOwner && (m.roles || []).includes("admin") && !isSelf(m)) return false; // co-admin can't edit other admins
    return true;
  };
  const canRemoveTarget = (m) => {
    if (!canRemove) return false;
    if (isSelf(m) || m.is_owner) return false;
    if (!isOwner && (m.roles || []).includes("admin")) return false;
    return true;
  };

  const filtered = members.filter((m) => [m.name, m.email, m.company].filter(Boolean).join(" ").toLowerCase().includes(query.toLowerCase()));
  const adminCount = members.filter((m) => m.is_owner || (m.roles || []).includes("admin")).length;
  const noPasswordCount = members.filter((m) => !m.has_password && (m.auth_provider || "password") === "password").length;
  const unsentCount = members.filter((m) => !m.has_password && (m.auth_provider || "password") === "password" && !m.invite_sent_at).length;

  if (loading) return <SectionLoader />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">Members</h1>
          <p className="mt-1 text-muted-foreground tabular-nums" data-testid="admin-members-summary">{members.length} total · {adminCount} admin{adminCount === 1 ? "" : "s"}{noPasswordCount > 0 ? ` · ${noPasswordCount} haven’t set a password yet` : ""}</p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
          {canRemove && noPasswordCount > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" disabled={bulkBusy} className="border-primary/40 text-primary hover:bg-primary/5" data-testid="admin-members-bulk-invite-button"><MailPlus className="mr-2 h-4 w-4" />Email set-password links ({unsentCount > 0 ? unsentCount : noPasswordCount})</Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="bulk-invite-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Email “set your password” links?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {noPasswordCount} member{noPasswordCount === 1 ? " hasn’t" : "s haven’t"} set a password yet{unsentCount < noPasswordCount ? `; ${noPasswordCount - unsentCount} already received a link` : ""}. Each person gets one email with a 14-day link, sent about a second apart. This is the moment they learn about the new site — make sure you’re ready.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
                  <AlertDialogCancel data-testid="bulk-invite-cancel">Cancel</AlertDialogCancel>
                  {unsentCount < noPasswordCount && (
                    <AlertDialogAction onClick={() => sendAllInvites(false)} className="bg-secondary text-foreground hover:bg-secondary/80" data-testid="bulk-invite-send-all">Resend to all {noPasswordCount}</AlertDialogAction>
                  )}
                  {unsentCount > 0 && (
                    <AlertDialogAction onClick={() => sendAllInvites(true)} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="bulk-invite-send-unsent">Send to {unsentCount} who haven’t received one</AlertDialogAction>
                  )}
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {canRemove && (
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="admin-members-import-button"><Upload className="mr-2 h-4 w-4" />Import CSV</Button>
          )}
          <div className="relative w-full sm:w-72"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input className="pl-9" placeholder="Search members…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="admin-members-search-input" /></div>
        </div>
      </div>

      {/* Member join requests (approval gate) */}
      {canRemove && joinRequests.length > 0 && (
        <Card className="border-primary/40 bg-primary/5" data-testid="join-requests-panel">
          <CardContent className="p-4">
            <div className="mb-2 flex items-center gap-2 text-primary">
              <UserPlus className="h-4 w-4" />
              <span className="text-sm font-semibold">Member requests ({joinRequests.length})</span>
            </div>
            <div className="space-y-2">
              {joinRequests.map((r) => (
                <div key={r.user_id} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card p-2.5" data-testid="join-request-row">
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar className="h-9 w-9"><AvatarImage src={r.avatar || undefined} /><AvatarFallback className="bg-primary text-xs text-primary-foreground">{initials(r.name)}</AvatarFallback></Avatar>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">{r.name}</p>
                      <p className="truncate text-xs text-muted-foreground">{r.email}{r.referrer_name ? ` · invited by ${r.referrer_name}` : ""}</p>
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-1.5">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" disabled={busyJoin === r.user_id} className="text-muted-foreground hover:text-destructive" data-testid="join-request-decline"><X className="mr-1 h-3.5 w-3.5" />Decline</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Decline {r.name}?</AlertDialogTitle><AlertDialogDescription>They won't be able to access the portal. You can approve them later if you change your mind.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => declineJoin(r)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Decline</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <Button size="sm" disabled={busyJoin === r.user_id} onClick={() => approveJoin(r)} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="join-request-approve">
                      {busyJoin === r.user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Check className="mr-1 h-3.5 w-3.5" />Approve</>}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Podcast access requests */}
      {canManageRoles && requests.length > 0 && (
        <Card className="border-[rgba(201,119,46,0.4)] bg-[rgba(201,119,46,0.06)]" data-testid="podcast-requests-panel">
          <CardContent className="p-4">
            <div className="mb-2 flex items-center gap-2 text-[#8A4A18]">
              <Mic className="h-4 w-4" />
              <span className="text-sm font-semibold">Podcast access requests ({requests.length})</span>
            </div>
            <div className="space-y-2">
              {requests.map((req) => (
                <div key={req.request_id} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card p-2.5" data-testid="podcast-request-row">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{req.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{req.email}</p>
                  </div>
                  <div className="flex shrink-0 gap-1.5">
                    <Button size="sm" variant="ghost" disabled={busyReq === req.request_id} onClick={() => denyReq(req)} data-testid="podcast-request-deny"><X className="mr-1 h-3.5 w-3.5" />Deny</Button>
                    <Button size="sm" disabled={busyReq === req.request_id} onClick={() => approveReq(req)} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="podcast-request-approve"><Check className="mr-1 h-3.5 w-3.5" />Approve</Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-border bg-card"><CardContent className="p-0">
        <Table data-testid="admin-members-table">
          <TableHeader><TableRow><TableHead>Member</TableHead><TableHead className="hidden md:table-cell">Email</TableHead><TableHead>Roles</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.map((m) => {
              const editable = canEditTarget(m);
              const removable = canRemoveTarget(m);
              return (
                <TableRow key={m.user_id} className="hover:bg-secondary/40">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9"><AvatarImage src={m.avatar || m.picture || undefined} /><AvatarFallback className="bg-primary text-xs text-primary-foreground">{initials(m.name)}</AvatarFallback></Avatar>
                      <div><div className="font-medium text-foreground">{m.name}{isSelf(m) ? " (you)" : ""}</div>{m.company && <div className="text-xs text-muted-foreground">{m.company}</div>}</div>
                      {!m.has_password && (m.auth_provider || "password") === "password" && (
                        <span className="hidden rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 sm:inline-flex" title={m.invite_sent_at ? `Set-password link sent ${new Date(m.invite_sent_at).toLocaleDateString()}` : "No set-password link sent yet"} data-testid="admin-members-no-password-pill">
                          {m.invite_sent_at ? "Link sent" : "No password yet"}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden text-muted-foreground md:table-cell">{m.email}</TableCell>
                  <TableCell><RoleBadges roles={m.roles} is_owner={m.is_owner} podcast_eligible={m.podcast_eligible} showMember size="sm" /></TableCell>
                  <TableCell className="text-right">
                    {(canManageRoles || canRemove) ? (
                      (editable || removable) ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" data-testid="admin-members-row-actions"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {editable && (
                              <DropdownMenuItem onClick={() => setEditing({ ...m, _self: user?.user_id })} data-testid="admin-members-manage-roles"><Settings2 className="mr-2 h-4 w-4" />Manage access</DropdownMenuItem>
                            )}
                            {removable && (
                              <>
                                {editable && <DropdownMenuSeparator />}
                                {!m.has_password && (m.auth_provider || "password") === "password" ? (
                                  <DropdownMenuItem onClick={() => sendInviteLink(m)} disabled={resetBusy === m.user_id} data-testid="admin-members-send-invite"><MailPlus className="mr-2 h-4 w-4" />{m.invite_sent_at ? "Resend set-password link" : "Send set-password link"}</DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem onClick={() => sendResetLink(m)} disabled={resetBusy === m.user_id} data-testid="admin-members-send-reset"><MailCheck className="mr-2 h-4 w-4" />Send reset link</DropdownMenuItem>
                                )}
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <DropdownMenuItem onSelect={(e) => e.preventDefault()} disabled={resetBusy === m.user_id} data-testid="admin-members-temp-password"><KeyRound className="mr-2 h-4 w-4" />Set temporary password</DropdownMenuItem>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader><AlertDialogTitle>Set a temporary password for {m.name}?</AlertDialogTitle><AlertDialogDescription>This immediately signs {m.name} out everywhere and generates a one-time password you can share. If they have an email on file, we&rsquo;ll send it to them too.</AlertDialogDescription></AlertDialogHeader>
                                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => setTempPassword(m)} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]">Generate</AlertDialogAction></AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                                <DropdownMenuSeparator />
                                <AlertDialog>
                                  <AlertDialogTrigger asChild><DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-destructive focus:text-destructive"><Trash2 className="mr-2 h-4 w-4" />Remove member</DropdownMenuItem></AlertDialogTrigger>
                                  <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Remove {m.name}?</AlertDialogTitle><AlertDialogDescription>This deletes their account and data permanently.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(m)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Remove</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                                </AlertDialog>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <span className="inline-flex items-center text-muted-foreground" title={m.is_owner ? "This account is protected" : "Admins can't change another admin's access"}><Lock className="h-4 w-4" /></span>
                      )
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent></Card>

      <RoleDialog member={editing} actorIsOwner={isOwner} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      <ImportMembersDialog open={importOpen} onOpenChange={setImportOpen} onImported={load} />

      {/* One-time temporary password reveal */}
      <Dialog open={!!tempPwInfo} onOpenChange={(o) => !o && setTempPwInfo(null)}>
        <DialogContent className="sm:max-w-md" data-testid="temp-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Temporary password{tempPwInfo?.name ? ` — ${tempPwInfo.name}` : ""}</DialogTitle>
            <DialogDescription>
              {tempPwInfo?.emailed
                ? <>We also emailed it to <span className="font-medium text-foreground">{tempPwInfo?.email}</span>. Share it securely &mdash; it won&rsquo;t be shown again.</>
                : <>Share this securely &mdash; it won&rsquo;t be shown again. Ask them to change it after signing in.</>}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 rounded-lg border border-border bg-secondary/40 p-3">
            <code className="flex-1 break-all font-mono text-sm text-foreground" data-testid="temp-password-value">{tempPwInfo?.temp_password}</code>
            <Button size="icon" variant="secondary" onClick={copyTempPassword} aria-label="Copy temporary password" data-testid="temp-password-copy"><Copy className="h-4 w-4" /></Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setTempPwInfo(null)} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="temp-password-done">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
